import { Link, NavLink } from "react-router-dom";
import { Menu, X, Heart, ShoppingBag, Instagram, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { useWishlist } from "../lib/wishlist";
import { useCart } from "../lib/cart";
import SearchBox from "./SearchBox";
import { useCValue } from "../lib/cms";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const brandName = useCValue("brand.name", "Maggie Records");
  const logo = useCValue("image.logo.url", "/brand/maggie-logo.jpg");
  const instagram = useCValue("social.instagram", "");
  const { t, lang, setLang } = useI18n();
  const { ids: favIds } = useWishlist();
  const { ids: cartIds } = useCart();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const navItems = [
    { to: "/catalogo", label: t("nav.shop"), testid: "nav-catalog-link" },
    { to: "/articulos", label: lang === "en" ? "Recommended" : "Artículos", testid: "nav-articles-link" },
    { to: "/compramos-tus-discos", label: lang === "en" ? "We buy records" : "Compramos tus discos", testid: "nav-sell-link" },
    { to: "/newsletter", label: t("nav.subscribe"), testid: "nav-newsletter-link" },
    { to: "/sobre", label: t("nav.about"), testid: "nav-about-link" },
    { to: "/contacto", label: t("nav.contact"), testid: "nav-contact-link" },
  ];

  return (
    <header className="sticky top-0 z-40 bg-background border-b border-border" data-testid="site-header">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-14 md:h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link" className="flex items-center gap-2.5">
          {logo && <img src={logo} alt="" className="h-7 w-7 md:h-8 md:w-8 object-cover mix-blend-multiply" />}
          <span className="text-sm md:text-base font-medium" data-testid="wordmark">{brandName}</span>
        </Link>

        <SearchBox className="hidden lg:block flex-1 max-w-[280px] mx-6" />

        <nav className="hidden md:flex items-center gap-5 text-sm">
          {navItems.map((it) => (
            <NavLink
              key={it.to} to={it.to} data-testid={it.testid}
              className={({ isActive }) =>
                `transition-opacity hover:opacity-55 ${isActive ? "text-[hsl(var(--accent))]" : "text-foreground"}`
              }
            >
              {it.label}
            </NavLink>
          ))}

          <span className="text-muted-foreground/40">·</span>

          <NavLink to="/favoritos" data-testid="nav-wishlist-link" aria-label="Favoritos"
            className={({ isActive }) => `relative inline-flex items-center gap-1 hover:opacity-55 ${isActive ? "text-[hsl(var(--accent))]" : "text-foreground"}`}>
            <Heart size={15} className={favIds.length > 0 ? "fill-[hsl(var(--accent))] text-[hsl(var(--accent))]" : ""} />
            {favIds.length > 0 && <span className="text-xs tabular-nums" data-testid="wishlist-count">{favIds.length}</span>}
          </NavLink>

          <NavLink to="/carrito" data-testid="nav-cart-link" aria-label="Carrito"
            className={({ isActive }) => `relative inline-flex items-center gap-1 hover:opacity-55 ${isActive ? "text-[hsl(var(--accent))]" : "text-foreground"}`}>
            <ShoppingBag size={15} />
            {cartIds.length > 0 && <span className="text-xs tabular-nums" data-testid="cart-count">{cartIds.length}</span>}
          </NavLink>

          {instagram && <a
            href={instagram}
            target="_blank"
            rel="noreferrer"
            aria-label="Instagram"
            className="text-foreground hover:opacity-55 inline-flex items-center"
            data-testid="nav-instagram-link"
          >
            <Instagram size={15} />
          </a>}

          <span className="text-muted-foreground/40">·</span>
          <div className="flex items-center gap-1.5 text-xs" data-testid="lang-switcher">
            <button type="button" onClick={() => setLang("es")} className={lang === "es" ? "text-foreground" : "text-muted-foreground hover:text-foreground"} data-testid="lang-es">ES</button>
            <span className="text-muted-foreground/40">/</span>
            <button type="button" onClick={() => setLang("en")} className={lang === "en" ? "text-foreground" : "text-muted-foreground hover:text-foreground"} data-testid="lang-en">EN</button>
          </div>
        </nav>

        <div className="md:hidden flex items-center gap-1">
          <button type="button" onClick={() => setMobileSearch((v) => !v)} aria-label="Buscar" className="p-2" data-testid="mobile-search-toggle">
            <Search size={18} />
          </button>
          <Link to="/carrito" aria-label="Carrito" data-testid="mobile-cart-link" className="relative p-2 -mr-1">
            <ShoppingBag size={18} />
            {cartIds.length > 0 && (
              <span className="absolute -top-0 -right-0 bg-[hsl(var(--accent))] text-white text-[10px] rounded-full w-4 h-4 grid place-items-center tabular-nums">
                {cartIds.length}
              </span>
            )}
          </Link>
          <button type="button" onClick={() => setOpen((o) => !o)} className="p-2 -mr-2" aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Search on tablet (md) where the header is too narrow, and on mobile via the icon */}
      <div className={`${mobileSearch ? "block" : "hidden"} md:block lg:hidden border-t border-border px-5 md:px-10 py-2.5 bg-background`}>
        <SearchBox autoFocus={mobileSearch} onNavigate={() => setMobileSearch(false)} />
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background" data-testid="mobile-menu">
          <div className="flex flex-col px-5 py-6 gap-4 text-sm">
            {navItems.map((it) => (
              <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} data-testid={`mobile-${it.testid}`} className="text-foreground">
                {it.label}
              </NavLink>
            ))}
            <NavLink to="/favoritos" onClick={() => setOpen(false)} data-testid="mobile-nav-wishlist-link" className="text-foreground inline-flex items-center gap-2">
              <Heart size={14} className={favIds.length > 0 ? "fill-[hsl(var(--accent))] text-[hsl(var(--accent))]" : ""} />
              Favoritos {favIds.length > 0 && `(${favIds.length})`}
            </NavLink>
            {instagram && <a
              href={instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="mobile-nav-instagram-link"
              className="text-foreground inline-flex items-center gap-2"
            >
              <Instagram size={14} /> Instagram
            </a>}
            <div className="flex gap-3 pt-4 border-t border-border text-xs">
              <button onClick={() => setLang("es")} className={lang === "es" ? "text-foreground" : "text-muted-foreground"}>ES</button>
              <span className="text-muted-foreground">/</span>
              <button onClick={() => setLang("en")} className={lang === "en" ? "text-foreground" : "text-muted-foreground"}>EN</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
